import { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectCopyAssistEnabled } from 'core/userContext/selectors';
import { useClickOutside } from 'utils/hooks/useClickOutside';
import { CopyAssistButtonDropdown } from 'modules/ai/copyAssist/CopyAssistButtonDropdown';
import { CopyAssistTagNames } from 'modules/domains/copyAssist/copyAssist';
import { isMessageMediumCopyAssistantSupported } from 'modules/ai/copyAssist/utils';
import styled from 'styled-components';
import { TextArea } from 'ui/inputs';
import { StyledTextArea as TextAreaBase } from 'ui/inputs/textArea/TextArea.styles';
import { spacing } from 'ui/theme';
import { NegativeFill } from 'ui/icons';
import { ContentModals, ContentModalStateType, useModalContext } from 'utils/contexts/modal';
import { useDynamicContentBuilder } from 'modules/templateEditor/hooks/useDynamicContentBuilder';
import { OptOutDisclaimer } from 'modules/templateEditor/common/OptOutDisclaimer';
import {
  SettingsReadOnlyText,
  SettingsReadOnlyDisclaimer,
  createInsertHandler,
  getIsEditMode,
  useFieldErrorTheme,
  TextAreaFieldProps,
  displayContent,
} from 'modules/templateEditor/common';
import { useEditableFieldsContext } from '../../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../../context/QueryParamContext';

import { TextAreaFieldToolbar } from './TextAreaFieldToolbar';

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledTextArea = styled(TextArea)<{
  isDisabled: boolean;
  hasSecondaryText: boolean;
  hasError: boolean;
}>`
  min-height: ${({ theme }) => theme.spacing.utils.pxToRem(108)};
  padding-right: ${spacing.doubleHalf};
  word-break: break-word;
  border-width: ${({ theme, hasError }) => (hasError ? theme.spacing.utils.pxToRem(2) : '')};

  [data-test='textarea-suffix'] {
    margin-left: 0;
    ${({ hasSecondaryText }) => hasSecondaryText && 'word-break: break-word;'}
  }

  ${TextAreaBase} {
    overflow: scroll;

    ::-webkit-scrollbar {
      display: none; /* Chrome, Safari and Opera */
    }
    -ms-overflow-style: none; /* Edge */
    scrollbar-width: none; /* Firefox */
  };
`;

const NO_ICON = false;

export const TextAreaField = ({
  isDisabled = false,
  isUnset = false,
  mediumRef = null,
  hasSecondaryText = false,
  minHeight = 108,
  maxCharacters,
  onChangeHandler,
  mediumDataKey,
  mediumDataValue = '',
  placeholderText = '',
  hasError = false,
  hasWarning = false,
  showMergeTag = false,
  showMarkdownOptions = false,
}: TextAreaFieldProps) => {
  const { view } = useQueryParamContext();
  const { showModal } = useModalContext<ContentModalStateType>();
  const { isEditMode, selectedMessageMedium, setCurrentMediumField } = useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const strUnsetText =
    mediumDataValue && mediumDataValue.length > 0 ? mediumDataValue : displayContent.strUnsetText;

  const isTextAreaUnset = isUnset || !mediumDataValue;

  const errorTheme = useFieldErrorTheme(hasError, hasWarning);

  const textAreaFieldWrapperEl = useRef<HTMLDivElement>(null);

  // Ref of wrapper required to target Copy Assist btn dynamically
  const textAreaWrapperWithHandlebar = useRef<HTMLDivElement>(null);

  const isCopyAssistEnabled = useSelector(selectCopyAssistEnabled);

  const [isTextAreaFieldFocused, setIsTextAreaFieldFocused] = useState(false);

  const showCopyAssist =
    isTextAreaFieldFocused &&
    isCopyAssistEnabled &&
    isMessageMediumCopyAssistantSupported(selectedMessageMedium, mediumDataKey);

  useClickOutside(textAreaFieldWrapperEl, () => {
    setIsTextAreaFieldFocused(false);
  });

  const showSnippetsModal = () =>
    showModal({
      modal: ContentModals.InsertSnippet,
      mediumDataKey,
      mediumDataValue,
      mediumRef,
    });

  const insert = createInsertHandler(mediumRef, mediumDataValue, onChangeHandler);
  const { show: showDynamicContentBuilder } = useDynamicContentBuilder(insert);

  return (
    <>
      {isEditModeVisible ? (
        <TextAreaWrapper
          ref={textAreaFieldWrapperEl}
          onFocus={() => {
            setIsTextAreaFieldFocused(true);
          }}
        >
          <StyledTextArea
            isDisabled={isDisabled}
            inputRef={mediumRef}
            textAreaWrapperRef={textAreaWrapperWithHandlebar}
            autoGrowMinHeight={minHeight}
            data-test={`${mediumDataKey}-text-area-field`}
            hasSecondaryText={hasSecondaryText}
            maxLength={maxCharacters}
            onChange={onChangeHandler}
            placeholder={placeholderText}
            resize={'vertical'}
            alwaysShowSuffix
            Suffix={
              <TextAreaFieldToolbar
                hasSecondaryText={hasSecondaryText}
                onSnippetButtonClick={showSnippetsModal}
                onDynamicContentButtonClick={showDynamicContentBuilder}
                inputRef={mediumRef}
                mediumDataKey={mediumDataKey}
                showMergeTag={showMergeTag}
                showMarkdownOptions={showMarkdownOptions}
              />
            }
            value={mediumDataValue}
            errorTheme={errorTheme}
            hasError={hasError}
            hasIcon={NO_ICON}
          />
          {showCopyAssist && (
            <CopyAssistButtonDropdown
              targetRef={textAreaWrapperWithHandlebar}
              mediumDataKey={mediumDataKey}
              value={mediumDataValue}
              targetTagName={CopyAssistTagNames.WhatsAppTextArea}
              setCurrentMediumField={setCurrentMediumField}
              messageMedium={selectedMessageMedium}
              maxLength={maxCharacters}
            />
          )}
        </TextAreaWrapper>
      ) : (
        <>
          <SettingsReadOnlyText
            isUnset={isTextAreaUnset}
            $hasError={hasError}
            data-test={`${mediumDataKey}-body-ro`}
          >
            {hasError && <NegativeFill color={errorTheme?.errorColor} />}
            {strUnsetText}
          </SettingsReadOnlyText>
          {hasSecondaryText && (
            <SettingsReadOnlyDisclaimer isUnset={isTextAreaUnset} data-test="sms-disclaimer-ro">
              <OptOutDisclaimer isUnderlined={true} />
            </SettingsReadOnlyDisclaimer>
          )}
        </>
      )}
    </>
  );
};
